import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, FormGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import CopyReferralLink from '../../components/CopyReferralLink/CopyReferralLink.jsx';
import DatePicker from '../../components/DatePicker/DatePicker.jsx';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import { getAppPartner } from '../Auth/authSelectors.js';
import amountFormat from '../../utils/amountFormat.js';
import { formatTurnoverLots } from '../../utils/turnoverToLots.js';
import { DATE_PRESETS_CONFIG } from '../Statistics/statisticsUtils.js';
import PartnerNetworkGraph from './PartnerNetworkGraph.jsx';
import { getNetworkFilterCache, setNetworkFilterCache } from './partnerNetworkFilterCache.js';
import { mergeFlatNodes } from './partnerNetworkTreeUtils.js';
import {
    getNetworkInviteLink,
    getNetworkPartner,
    getNetworkTree,
} from './partnerNetworkApi.js';
import {
    NetworkFiltersRowStyled,
    NetworkInviteRowStyled,
    NetworkPageStyled,
    NetworkPartnerLabelStyled,
    NetworkPresetsStyled,
    NetworkSectionStyled,
    NetworkTableStyled,
    NetworkTableWrapStyled,
} from './styled/PartnerNetworkStyled';

const formatDate = (date) => (date ? moment(date).format('YYYY-MM-DD') : undefined);

const toCachedDate = (date) => (date ? date.toISOString() : null);

const fromCachedDate = (value, fallback) => {
    if (value === undefined) {
        return fallback();
    }
    return value ? new Date(value) : null;
};

const EMPTY_STATS = {
    clients_count: 0,
    registrations: 0,
    ftd: 0,
    ftd_amount: 0,
    deposits: 0,
    deposit_volume: 0,
    trading_volume: 0,
    commission: 0,
};

function PartnerNetwork() {
    const { getLangPath } = useLangContext();
    const partnerInfo = useSelector(getAppPartner);
    const cachedFilters = getNetworkFilterCache();
    const [startDate, setStartDate] = useState(() => (
        fromCachedDate(cachedFilters.startDate, () => moment().subtract(30, 'days').toDate())
    ));
    const [endDate, setEndDate] = useState(() => (
        fromCachedDate(cachedFilters.endDate, () => new Date())
    ));
    const [selectedPartnerUid, setSelectedPartnerUid] = useState(
        () => cachedFilters.selectedPartnerUid ?? null,
    );
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerStats, setPartnerStats] = useState(EMPTY_STATS);
    const [treeNodes, setTreeNodes] = useState([]);
    const [rootExpectedPayout, setRootExpectedPayout] = useState('0');
    const [inviteLink, setInviteLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);

    const viewerUid = partnerInfo?.partner_uid ?? null;
    const activePartnerUid = selectedPartnerUid || viewerUid;

    const branchRoot = useMemo(() => {
        if (!partnerInfo?.partner_uid) {
            return null;
        }
        return {
            partner_uid: partnerInfo.partner_uid,
            full_name: partnerInfo.full_name,
            email: partnerInfo.email,
            active: true,
            depth: 0,
            expected_payout: rootExpectedPayout,
        };
    }, [partnerInfo, rootExpectedPayout]);

    const dateParams = useMemo(
        () => ({
            start_date: formatDate(startDate),
            end_date: formatDate(endDate),
        }),
        [startDate, endDate],
    );

    const selectedLevel = useMemo(() => {
        if (!activePartnerUid) {
            return 0;
        }
        if (activePartnerUid === viewerUid) {
            return 0;
        }
        const node = treeNodes.find((item) => item.partner_uid === activePartnerUid);
        if (node?.depth != null) {
            return node.depth;
        }
        if (selectedPartner?.level != null) {
            return selectedPartner.level;
        }
        return null;
    }, [activePartnerUid, viewerUid, treeNodes, selectedPartner]);

    const loadTree = useCallback(async (depth = 2) => {
        const tree = await getNetworkTree({ depth, ...dateParams });
        setTreeNodes(tree.nodes || []);
        if (tree.root?.expected_payout != null) {
            setRootExpectedPayout(tree.root.expected_payout);
        }
        return tree.nodes || [];
    }, [dateParams]);

    const loadPartnerStats = useCallback(async (partnerUid) => {
        if (!partnerUid) {
            return;
        }
        setStatsLoading(true);
        try {
            const detail = await getNetworkPartner(partnerUid, dateParams);
            setSelectedPartner(detail);
            setPartnerStats(detail.stats || EMPTY_STATS);
        } finally {
            setStatsLoading(false);
        }
    }, [dateParams]);

    const loadPage = useCallback(async () => {
        setLoading(true);
        try {
            const [, invite] = await Promise.all([
                loadTree(),
                getNetworkInviteLink(),
            ]);
            setInviteLink(invite.url || '');
        } finally {
            setLoading(false);
        }
    }, [loadTree]);

    useEffect(() => {
        loadPage();
    }, [loadPage]);

    useEffect(() => {
        if (viewerUid && selectedPartnerUid == null) {
            setSelectedPartnerUid(viewerUid);
        }
    }, [viewerUid, selectedPartnerUid]);

    useEffect(() => {
        if (activePartnerUid) {
            loadPartnerStats(activePartnerUid);
        }
    }, [activePartnerUid, loadPartnerStats]);

    useEffect(() => {
        setNetworkFilterCache({
            startDate: toCachedDate(startDate),
            endDate: toCachedDate(endDate),
            selectedPartnerUid,
        });
    }, [startDate, endDate, selectedPartnerUid]);

    const handleSelectPartner = useCallback((uid) => {
        setSelectedPartnerUid(uid);
    }, []);

    const handleExpandNode = useCallback(async (uid) => {
        const res = await getNetworkTree({ root_partner_uid: uid, depth: 2, ...dateParams });
        setTreeNodes((prev) => mergeFlatNodes(prev, res.nodes || []));
    }, [dateParams]);

    const handleStartDateChange = useCallback(
        (date) => {
            setStartDate(date);
            if (date && endDate && moment(endDate).isBefore(moment(date))) {
                setEndDate(date);
            }
        },
        [endDate],
    );

    const handlePresetClick = useCallback((e) => {
        const presetName = e.currentTarget.dataset.name;
        const preset = DATE_PRESETS_CONFIG.find((item) => item.name === presetName);
        setStartDate(preset?.startDate()?.toDate() || null);
        setEndDate(preset?.endDate()?.toDate() || null);
    }, []);

    const partnerLabelName = selectedPartner?.full_name
        || (activePartnerUid === viewerUid ? partnerInfo?.full_name : null)
        || `#${activePartnerUid || ''}`;

    return (
        <NetworkPageStyled>
            <div className="page-header">
                <span className="section-eyebrow">{getLangPath('network.eyebrow')}</span>
                <h1 className="page-title">{getLangPath('network.title')}</h1>
                <p className="page-desc">{getLangPath('network.desc')}</p>
            </div>

            <NetworkSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('network.invite_step')}</span>
                        <h2 className="section-title">{getLangPath('network.invite_title')}</h2>
                        <p className="section-desc">{getLangPath('network.invite_desc')}</p>
                    </div>
                </div>
                <NetworkInviteRowStyled>
                    <Form.Control readOnly value={inviteLink} />
                    {inviteLink && <CopyReferralLink referralLink={inviteLink} iconSize="16px" />}
                </NetworkInviteRowStyled>
            </NetworkSectionStyled>

            <NetworkSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('network.stats_step')}</span>
                        <h2 className="section-title">{getLangPath('network.stats_title')}</h2>
                        <p className="section-desc">{getLangPath('network.stats_desc')}</p>
                    </div>
                </div>

                <NetworkFiltersRowStyled>
                    <FormGroup>
                        <Form.Label>{getLangPath('network.start_date')}</Form.Label>
                        <DatePicker
                            selectedDate={startDate}
                            onChange={handleStartDateChange}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="DD/MM/YYYY"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Form.Label>{getLangPath('network.end_date')}</Form.Label>
                        <DatePicker
                            selectedDate={endDate}
                            onChange={setEndDate}
                            minDate={startDate || undefined}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="DD/MM/YYYY"
                        />
                    </FormGroup>
                    <div className="filter-actions">
                        <Button
                            variant="primary"
                            onClick={() => loadPartnerStats(activePartnerUid)}
                            disabled={statsLoading || !activePartnerUid}
                        >
                            {getLangPath('network.refresh')}
                        </Button>
                    </div>
                </NetworkFiltersRowStyled>

                <FormGroup>
                    <Form.Label className="mb-2">{getLangPath('statistics.date_presets')}</Form.Label>
                    <NetworkPresetsStyled>
                        {DATE_PRESETS_CONFIG.map((preset) => (
                            <Button
                                key={preset.name}
                                variant="link"
                                type="button"
                                data-name={preset.name}
                                onClick={handlePresetClick}
                            >
                                {getLangPath(preset.title)}
                            </Button>
                        ))}
                    </NetworkPresetsStyled>
                </FormGroup>

                {activePartnerUid && (
                    <NetworkPartnerLabelStyled>
                        <h3 className="partner-name">{partnerLabelName}</h3>
                        <p className="partner-meta">
                            {`#${activePartnerUid}`}
                            {selectedLevel != null ? ` · L${selectedLevel}` : ''}
                            {selectedPartner?.created_at
                                ? ` · ${getLangPath('network.partner_registered')}: ${moment(selectedPartner.created_at).format('DD/MM/YYYY')}`
                                : ''}
                        </p>
                    </NetworkPartnerLabelStyled>
                )}

                <NetworkTableWrapStyled>
                    <NetworkTableStyled>
                        <thead>
                            <tr>
                                <th className="col-metric">{getLangPath('network.col_clients')}</th>
                                <th className="col-metric">{getLangPath('network.col_registered')}</th>
                                <th className="col-metric">{getLangPath('network.col_ftd')}</th>
                                <th className="col-money">{getLangPath('network.col_ftd_amount')}</th>
                                <th className="col-metric">{getLangPath('network.col_deposits')}</th>
                                <th className="col-money">{getLangPath('network.col_deposits_amount')}</th>
                                <th className="col-money">{getLangPath('network.col_turnover')}</th>
                                <th className="col-money">{getLangPath('network.col_payout')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="col-metric">{partnerStats.clients_count ?? 0}</td>
                                <td className="col-metric">{partnerStats.registrations ?? 0}</td>
                                <td className="col-metric">{partnerStats.ftd ?? 0}</td>
                                <td className="col-money">${amountFormat(partnerStats.ftd_amount)}</td>
                                <td className="col-metric">{partnerStats.deposits ?? 0}</td>
                                <td className="col-money">${amountFormat(partnerStats.deposit_volume)}</td>
                                <td className="col-money">{formatTurnoverLots(partnerStats.trading_volume)}</td>
                                <td className="col-money">${amountFormat(partnerStats.commission)}</td>
                            </tr>
                        </tbody>
                    </NetworkTableStyled>
                </NetworkTableWrapStyled>
            </NetworkSectionStyled>

            <NetworkSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('network.tree_step')}</span>
                        <h2 className="section-title">{getLangPath('network.tree_title')}</h2>
                        <p className="section-desc">{getLangPath('network.tree_desc')}</p>
                    </div>
                </div>

                <PartnerNetworkGraph
                    flatNodes={treeNodes}
                    branchRoot={branchRoot}
                    viewerUid={viewerUid}
                    selectedPartnerUid={activePartnerUid}
                    loading={loading}
                    onSelectPartner={handleSelectPartner}
                    onExpandNode={handleExpandNode}
                />
            </NetworkSectionStyled>
        </NetworkPageStyled>
    );
}

export default PartnerNetwork;
